import pilotSilhouette from "asset/image/pilot/pilot_silhouette.png"
import pilotTitle from "asset/image/pilot/pilot_title.png"
import particles1 from "asset/image/pilot/particles1.png"
import particles2 from "../../asset/image/pilot/particles2.png";
import logo from "asset/logo/perplay_logo_white.png"
import {useEffect, useLayoutEffect, useRef, useState} from "react";
import GearFooter from "../Footer/GearFooter";
import moment from "moment";
import {useTranslation} from "react-i18next";
import {useServerTime} from "../../hooks/useServerTime";
import {useMediaQuery} from "react-responsive";
import PilotGrowth from "./PilotGrowth";
import PilotGrowthMobile from "./PilotGrowthMobile";
import PilotOverview from "./PilotOverview";
import {isMobile, isTablet} from "react-device-detect";
import PilotSkill from "./PilotSkill";
import PilotSkillMobile from "./PilotSkillMobile";
import PilotAirdrop from "./PilotAirdrop";
import PilotAirdropMobile from "./PilotAirdropMobile";

const PilotPage = (props) => {
    const { data } = props; // {config: any, maintenance: boolean}

    const isSmallWidth = useMediaQuery({ maxWidth: 1200 });
    const userAgent = navigator.userAgent.toLowerCase();
    const isSamsungFoldable = /sm-f/i.test(userAgent);

    const {getServerTimeISO} = useServerTime();
    const serverTime = moment(getServerTimeISO());

    useEffect(() => {
        console.log("Server time: ", serverTime.format("YYYY-MM-DD HH:mm:ss"));
    }, [serverTime])

    const { t, i18n } = useTranslation();
    const [currentLanguage, setCurrentLanguage] = useState("");

    const [config, setConfig] = useState(null);
    const [schedule, setSchedule] = useState(null);

    const changeLanguage = (lang) => {
        i18n.changeLanguage(lang);
        setCurrentLanguage(i18n.language);
    };

    useEffect(() => {
        if(!data){
            return;
        }
        setConfig(data?.config);
    }, [data]);


    useEffect(() => {
        if(!config){
            console.log("Config is null.");
            return;
        }

        console.log("Config loaded.");
        setSchedule({
            overview : moment(config.overview?.startAt),
            growth : moment(config.growth?.startAt),
            skill : moment(config.skill?.startAt),
            airdrop : moment(config.airdrop?.startAt),
            minting : moment(config.minting?.startAt),
        });
    }, [config]);

    const introRef = useRef(null);
    const overviewRef = useRef(null);
    const growthRef = useRef(null);
    const skillRef = useRef(null);
    const airdropRef = useRef(null);

    const [currentSection, setCurrentSection] = useState("overview");

    useEffect(() => {
        if(i18n.isInitialized){
            if(!i18n.language.includes("ko") && !i18n.language.includes("en")){
                i18n.changeLanguage("en").then(() => {
                    setCurrentLanguage("en")
                });
                return;
            }

            setCurrentLanguage(i18n.language);

        }
    }, [i18n]);

    const [isInitialLoad, setIsInitialLoad] = useState(true);


    const observerInit = () => {

        // 화면을 가장 많이 차지하는 섹션
        const findMostVisibleSection = () => {
            const sections = [
                { ref: overviewRef, name: 'overview' },
                { ref: growthRef, name: 'growth' },
                { ref: skillRef, name: 'skill' },
                { ref: airdropRef, name: 'airdrop' }
            ];

            const viewportHeight = window.innerHeight;
            let mostVisibleSection = null;
            let maxVisibleRatio = 0;

            sections.forEach(section => {
                if (section.ref.current) {
                    const rect = section.ref.current.getBoundingClientRect();

                    // 더 정확한 가시성 계산
                    const visibleTop = Math.max(rect.top, 0);
                    const visibleBottom = Math.min(rect.bottom, viewportHeight);
                    const visibleHeight = Math.max(0, visibleBottom - visibleTop);

                    const visibleRatio = visibleHeight / rect.height;

                    if (visibleRatio > maxVisibleRatio) {
                        maxVisibleRatio = visibleRatio;
                        mostVisibleSection = section;
                    }
                }
            });

            return { mostVisibleSection, maxVisibleRatio };
        };

        // IntersectionObserver 미지원 시
        const fallbackObserver = () => {
            const checkVisibility = () => {
                const { mostVisibleSection, maxVisibleRatio } = findMostVisibleSection();

                if (mostVisibleSection && maxVisibleRatio > 0.1) {
                    setCurrentSection(mostVisibleSection.name);
                }
            };

            window.addEventListener('scroll', checkVisibility);
            window.addEventListener('resize', checkVisibility);

            // 초기 한 번 실행
            checkVisibility();

            // 클린업
            return () => {
                window.removeEventListener('scroll', checkVisibility);
                window.removeEventListener('resize', checkVisibility);
            };
        };

        // IntersectionObserver 지원 브라우저
        const standardObserver = () => {
            const sectionObserver = new IntersectionObserver(
                (entries) => {
                    const visibleSections = entries
                        .filter(entry => entry.isIntersecting)
                        .map(entry => {
                            let sectionName = "";

                            switch(entry.target){
                                // case introRef.current:
                                //     sectionName = "intro";
                                //     break;
                                case overviewRef.current:
                                    sectionName = "overview";
                                    break;
                                case growthRef.current:
                                    sectionName = "growth";
                                    break;
                                case skillRef.current:
                                    sectionName = "skill";
                                    break;
                                case airdropRef.current:
                                    sectionName = "airdrop";
                                    break;
                                default:
                                    sectionName = "overview";
                            }

                            return {
                                sectionName,
                                visibilityRatio: entry.intersectionRatio
                            };
                        })
                        .filter(section => section.sectionName);

                    if(visibleSections.length > 0) {
                        const mostVisibleSection = visibleSections.reduce(
                            (prev, current) => (
                                prev.visibilityRatio > current.visibilityRatio ? prev : current
                            )
                        );

                        // 임계값 조정
                        const minThreshold = isSmallWidth ? 0.1 : 0.3;
                        if(mostVisibleSection.visibilityRatio >= minThreshold) {
                            setCurrentSection(mostVisibleSection.sectionName);
                        }
                    }
                },
                {
                    threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1],
                    rootMargin: '0px 0px 0px 0px'
                }
            );

            const contentRefs = [overviewRef, growthRef, skillRef, airdropRef];
            contentRefs.forEach(ref => {
                if (ref.current) {
                    sectionObserver.observe(ref.current);
                }
            });

            return () => {
                contentRefs.forEach(ref => {
                    if (ref.current) {
                        sectionObserver.unobserve(ref.current);
                    }
                });
            };
        };

        if ('IntersectionObserver' in window) {
            return standardObserver();
        } else {
            return fallbackObserver();
        }
    };

    useLayoutEffect(() => {

        observerInit();

        // 옵저버 오류 방지
        const lateInit = setTimeout(() => {
            observerInit();
        }, 2000);

        return () => clearTimeout(lateInit);

    }, [
        isInitialLoad,
        overviewRef,
        growthRef,
        skillRef,
        airdropRef
    ]);


    const handleNavigatorClick = (sectionName) => {
        const element = document.getElementById(sectionName);

        if(!element) {
            alert("공개 예정입니다.")
            return;
        }

        element.scrollIntoView({behavior: "smooth"});
    }

    useEffect(() => {
        console.log("schedule: ", schedule);
    }, [schedule, config])

    if(!schedule){
        return null;
    }

    return (
        <>
            <section className="particles">
                {
                    isSmallWidth ?
                        <img src={particles2} alt="" loading="lazy" />
                        : <img src={particles1} alt="" loading="lazy" />
                }
            </section>

            <header className={"header"}>
                <a className="header-logo" href={"https://perplay.io"}>
                    <img src={logo} alt="perplay"/>
                </a>

                <div className={"lang-btn-container"}>
                    <button
                        className={currentLanguage.includes("en")  ? "active" : ""}
                        onClick={() => {changeLanguage("en")}}
                    >
                        EN
                    </button>
                    <div className={"separate-line"} />
                    <button
                        className={currentLanguage.includes("ko") ? "active" : ""}
                        onClick={() => {changeLanguage("ko")}}
                    >
                        KO
                    </button>

                </div>
            </header>

            <section className={"intro"} ref={introRef}>
                {
                    schedule?.minting <= serverTime ?
                        isMobile || isTablet || isSamsungFoldable ?
                            <PilotAirdropMobile language={currentLanguage}/>
                            : <PilotAirdrop language={currentLanguage}/>
                        :
                        <>
                        <article className="title">
                            <img className={`title-img`} src={pilotTitle} alt=""/>

                            <div className="glow-container">
                                <div className="glow1"/>
                                <div className="glow2"/>
                            </div>

                            <img className={`pilot-silhouette`} src={pilotSilhouette} alt=""    style={{
                            }}/>

                            <div className="back-glow-container">
                                <div className="glow1"/>
                                <div className="glow2"/>
                            </div>

                        </article>

                        <article className="schedule-container">
                            <div className="schedule">
                                <div className={`schedule-date ${schedule?.overview <= serverTime  ? "active" : ""}`}>
                                    {
                                       schedule?.overview?.format("MM.DD")
                                    }
                                </div>
                                <div className={`schedule-desc ${schedule?.overview <= serverTime  ? "active" : ""}`}>
                                    {
                                        t("PILOT_OVERVIEW")
                                    }
                                </div>
                            </div>

                            <div className="schedule">
                                <div className={`schedule-date ${schedule?.growth <= serverTime  ? "active" : ""}`}>
                                    {
                                        schedule?.growth?.format("MM.DD")
                                    }
                                </div>
                                <div className={`schedule-desc ${schedule?.growth <= serverTime  ? "active" : ""}`}>
                                    {
                                        schedule?.growth <= serverTime ?
                                            t("PILOT_GROWTH_IS_OPEN")
                                            : t("PILOT_GROWTH_TO_OPEN")
                                    }
                                </div>
                            </div>

                            <div className="schedule">
                                <div className={`schedule-date ${schedule?.skill <= serverTime  ? "active" : ""}`}>
                                    {
                                        schedule?.skill?.format("MM.DD")
                                    }
                                </div>
                                <div className={`schedule-desc ${schedule?.skill <= serverTime  ? "active" : ""}`}>
                                    {
                                        schedule?.skill <= serverTime ?
                                            t("PILOT_SKILL_IS_OPEN")
                                            : t("PILOT_SKILL_TO_OPEN")
                                    }
                                </div>
                            </div>

                            <div className="schedule">
                                <div className={`schedule-date soon ${schedule?.airdrop <= serverTime  ? "active" : ""}`}>
                                    {
                                        schedule?.airdrop?.format("MM.DD")
                                    }
                                </div>
                                <div className={`schedule-desc ${schedule?.airdrop <= serverTime  ? "active" : ""}`}>
                                    {
                                        // schedule?.airdrop <= serverTime ?
                                            t("PILOT_AIRDROP_IS_OPEN")
                                            // : t("PILOT_AIRDROP_TO_OPEN")
                                    }
                                </div>
                            </div>
                        </article>
                        </>
                }
            </section>

            {/*<section className="intro" ref={introRef}>*/}

            {/*    <article className="title">*/}
            {/*        <img className={`title-img`} src={pilotTitle} alt=""/>*/}

            {/*        <div className="glow-container">*/}
            {/*            <div className="glow1"/>*/}
            {/*            <div className="glow2"/>*/}
            {/*        </div>*/}

            {/*        <img className={`pilot-silhouette`} src={pilotSilhouette} alt=""    style={{*/}
            {/*        }}/>*/}

            {/*        <div className="back-glow-container">*/}
            {/*            <div className="glow1"/>*/}
            {/*            <div className="glow2"/>*/}
            {/*        </div>*/}

            {/*    </article>*/}

            {/*        <article className="schedule-container">*/}
            {/*            <div className="schedule">*/}
            {/*                <div className={`schedule-date ${schedule?.overview <= serverTime  ? "active" : ""}`}>*/}
            {/*                    {*/}
            {/*                       schedule?.overview?.format("MM.DD")*/}
            {/*                    }*/}
            {/*                </div>*/}
            {/*                <div className={`schedule-desc ${schedule?.overview <= serverTime  ? "active" : ""}`}>*/}
            {/*                    {*/}
            {/*                        t("PILOT_OVERVIEW")*/}
            {/*                    }*/}
            {/*                </div>*/}
            {/*            </div>*/}

            {/*            <div className="schedule">*/}
            {/*                <div className={`schedule-date ${schedule?.growth <= serverTime  ? "active" : ""}`}>*/}
            {/*                    {*/}
            {/*                        schedule?.growth?.format("MM.DD")*/}
            {/*                    }*/}
            {/*                </div>*/}
            {/*                <div className={`schedule-desc ${schedule?.growth <= serverTime  ? "active" : ""}`}>*/}
            {/*                    {*/}
            {/*                        schedule?.growth <= serverTime ?*/}
            {/*                            t("PILOT_GROWTH_IS_OPEN")*/}
            {/*                            : t("PILOT_GROWTH_TO_OPEN")*/}
            {/*                    }*/}
            {/*                </div>*/}
            {/*            </div>*/}

            {/*            <div className="schedule">*/}
            {/*                <div className={`schedule-date ${schedule?.skill <= serverTime  ? "active" : ""}`}>*/}
            {/*                    {*/}
            {/*                        schedule?.skill?.format("MM.DD")*/}
            {/*                    }*/}
            {/*                </div>*/}
            {/*                <div className={`schedule-desc ${schedule?.skill <= serverTime  ? "active" : ""}`}>*/}
            {/*                    {*/}
            {/*                        schedule?.skill <= serverTime ?*/}
            {/*                            t("PILOT_SKILL_IS_OPEN")*/}
            {/*                            : t("PILOT_SKILL_TO_OPEN")*/}
            {/*                    }*/}
            {/*                </div>*/}
            {/*            </div>*/}

            {/*            <div className="schedule">*/}
            {/*                <div className={`schedule-date soon ${schedule?.airdrop <= serverTime  ? "active" : ""}`}>*/}
            {/*                    {*/}
            {/*                        t("LATER_ANNOUNCE")*/}
            {/*                    }*/}
            {/*                </div>*/}
            {/*                <div className={`schedule-desc ${schedule?.airdrop <= serverTime  ? "active" : ""}`}>*/}
            {/*                    {*/}
            {/*                        schedule?.airdrop <= serverTime ?*/}
            {/*                            t("PILOT_AIRDROP_IS_OPEN")*/}
            {/*                            : t("PILOT_AIRDROP_TO_OPEN")*/}
            {/*                    }*/}
            {/*                </div>*/}
            {/*            </div>*/}
            {/*        </article>*/}


            {/*</section>*/}


            <section className="navigator">
                    <div className="navigator-button-container">
                        <a
                            onClick={() => {window.scrollTo(0,0)} }
                            className={`active
                         `}
                            style={{display : schedule.minting <= serverTime ? "block" : "none"}}
                        >
                            {t("PILOT_AIRDROP")}
                        </a>
                        <a
                            onClick={() => {handleNavigatorClick("overview")}}
                            className={`
                           ${schedule.overview <= serverTime ? "active" : ""} 
                           ${currentSection === "overview" ? "current" : ""}
                         `}>
                            {t("OVERVIEW")}
                        </a>
                        <a
                            onClick={() => {handleNavigatorClick("growth")}}
                            className={`
                           ${schedule.growth <= serverTime ? "active" : ""}
                           ${currentSection === "growth" ? "current" : ""}
                         `}>
                            {t("PILOT_GROWTH")}
                        </a>
                        <a
                            onClick={() => {handleNavigatorClick("skill")}}
                            className={`
                           ${schedule.skill <= serverTime ? "active" : ""}
                           ${currentSection === "skill" ? "current" : ""}
                         `}>
                            {t("PILOT_SKILL")}
                        </a>
                        {/*<a*/}
                        {/*    onClick={() => {handleNavigatorClick("airdrop")}}*/}
                        {/*    className={`*/}
                        {/*   ${schedule.airdrop <= serverTime ? "active" : ""}*/}
                        {/*   ${currentSection === "airdrop" ? "current" : ""}*/}
                        {/* `}>*/}
                        {/*    {t("PILOT_AIRDROP")}*/}
                        {/*</a>*/}
                    </div>
            </section>


            <section
                className="overview"
                id={"overview"}
                ref={overviewRef}
            >
                <PilotOverview/>
            </section>


            <section
                id={"growth"}
                className={"growth"}
                ref={growthRef}
            >
                {
                    isMobile || isSmallWidth ?
                        <PilotGrowthMobile serverTime={serverTime} revealTime={schedule.growth} language={currentLanguage} />
                        : <PilotGrowth serverTime={serverTime} revealTime={schedule.growth} language={currentLanguage} />
                }
            </section>


            <section
                id={"skill"}
                className={"skill"}
                ref={skillRef}
            >
                {
                    isMobile || isSmallWidth ?
                        <PilotSkillMobile serverTime={serverTime} revealTime={schedule.skill} language={currentLanguage} />
                        : <PilotSkill serverTime={serverTime} revealTime={schedule.skill} language={currentLanguage}/>
                }
            </section>





            <GearFooter/>
        </>

    )
}

export default PilotPage;
